import { useState, useEffect, useCallback } from 'react';
import { 
  initDB, 
  savePendingAttendance, 
  getPendingAttendance, 
  markAttendanceSynced,
  saveOfflineLesson,
  getOfflineLessons,
  removeOfflineLesson,
  getStorageUsage
} from '../utils/offlineDB';

export const useOffline = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [offlineLessons, setOfflineLessons] = useState([]);
  const [storageUsage, setStorageUsage] = useState({ usage: 0, quota: 0, percentUsed: 0 });

  // Initialize IndexedDB
  useEffect(() => {
    initDB().catch(console.error);
  }, []);

  // Online/Offline detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingData();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load pending sync count
  const updatePendingCount = useCallback(async () => {
    try {
      const pending = await getPendingAttendance();
      setPendingSync(pending.length);
    } catch (error) {
      console.error('Error getting pending attendance:', error);
    }
  }, []);

  // Load offline lessons
  const loadOfflineLessons = useCallback(async () => {
    try {
      const lessons = await getOfflineLessons();
      setOfflineLessons(lessons);
    } catch (error) {
      console.error('Error getting offline lessons:', error);
    }
  }, []);

  // Update storage usage
  const updateStorageUsage = useCallback(async () => {
    try {
      const usage = await getStorageUsage();
      setStorageUsage(usage);
    } catch (error) {
      console.error('Error getting storage usage:', error);
    }
  }, []);

  useEffect(() => {
    updatePendingCount();
    loadOfflineLessons();
    updateStorageUsage();
  }, [updatePendingCount, loadOfflineLessons, updateStorageUsage]);

  // Sync pending data when back online
  const syncPendingData = async () => {
    if (!navigator.onLine) return;

    try {
      const pending = await getPendingAttendance();
      
      for (const record of pending) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/attendance/bulk`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${record.token}`
            },
            body: JSON.stringify(record.data)
          });

          if (response.ok) {
            await markAttendanceSynced(record.id);
          }
        } catch (error) {
          console.error('Error syncing record:', record.id, error);
        }
      }
      
      await updatePendingCount();
    } catch (error) {
      console.error('Error syncing pending data:', error);
    }
  };

  // Save attendance offline
  const saveAttendanceOffline = async (classId, date, attendanceData, token) => {
    try {
      await savePendingAttendance(classId, date, attendanceData, token);
      await updatePendingCount();
      
      // Request background sync if available
      if ('serviceWorker' in navigator && 'sync' in window.registration) {
        try {
          await window.registration.sync.register('sync-attendance');
        } catch (error) {
          console.log('Background sync not available');
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error saving attendance offline:', error);
      return false;
    }
  };

  // Download lesson for offline
  const downloadLessonOffline = async (lesson) => {
    try {
      await saveOfflineLesson(lesson);
      await loadOfflineLessons();
      await updateStorageUsage();
      return true;
    } catch (error) {
      console.error('Error downloading lesson:', error);
      return false;
    }
  };

  // Remove offline lesson
  const removeLessonOffline = async (lessonId) => {
    try {
      await removeOfflineLesson(lessonId);
      await loadOfflineLessons();
      await updateStorageUsage();
      return true;
    } catch (error) {
      console.error('Error removing lesson:', error);
      return false;
    }
  };

  // Check if lesson is available offline
  const isLessonOffline = (lessonId) => {
    return offlineLessons.some(l => l.lesson_id === lessonId);
  };

  return {
    isOnline,
    pendingSync,
    offlineLessons,
    storageUsage,
    saveAttendanceOffline,
    syncPendingData,
    downloadLessonOffline,
    removeLessonOffline,
    isLessonOffline,
    updatePendingCount
  };
};

// Service Worker Registration Hook
export const useServiceWorker = () => {
  const [swRegistration, setSwRegistration] = useState(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('ServiceWorker registered:', registration);
          setSwRegistration(registration);

          // Check for updates
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          });
        })
        .catch((error) => {
          console.error('ServiceWorker registration failed:', error);
        });
    }
  }, []);

  const updateServiceWorker = () => {
    if (swRegistration && swRegistration.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  };

  return { swRegistration, updateAvailable, updateServiceWorker };
};

// Push Notification Hook
export const usePushNotifications = () => {
  const [permission, setPermission] = useState(Notification.permission);
  const [subscription, setSubscription] = useState(null);

  const requestPermission = async () => {
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const subscribeToPush = async (swRegistration) => {
    if (!swRegistration) return null;

    try {
      const existingSub = await swRegistration.pushManager.getSubscription();
      if (existingSub) {
        setSubscription(existingSub);
        return existingSub;
      }

      // Note: In production, you'd get this from your server
      const vapidPublicKey = 'your-vapid-public-key';
      
      const newSub = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
      
      setSubscription(newSub);
      return newSub;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return null;
    }
  };

  const showNotification = (title, options = {}) => {
    if (permission !== 'granted') return;
    
    new Notification(title, {
      icon: '/icon-192.png',
      badge: '/icon-72.png',
      ...options
    });
  };

  return { permission, subscription, requestPermission, subscribeToPush, showNotification };
};

// Utility function for VAPID key conversion
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
